// The playground's bottom panel, as in the owner's picture: PROBLEMS · OUTPUT · TERMINAL · CONSOLE.
// PROBLEMS = what the system would refuse; OUTPUT = Save / Build / Make live / Discover steps; TERMINAL = a real shell
// in the sandbox; CONSOLE = every run in detail (the call the handler made, the reply used, the outcome, the system check).
import { useEffect, useRef } from 'react'
import { CircleX, Trash2, TriangleAlert, X } from 'lucide-react'
import type { Problem } from '@/lib/problems'
import { TerminalView } from './TerminalView'

export type PanelTab = 'problems' | 'output' | 'terminal' | 'console'

export interface BottomPanelProps {
  tab: PanelTab
  onTab: (t: PanelTab) => void
  onClose: () => void
  problems: Problem[]
  onProblem: (p: Problem) => void
  output: string[]
  console: string[]
  /** the shell's WebSocket address, null when the sandbox is not open */
  terminalUrl: string | null
  terminalNote?: string | null
  dark: boolean
  onClear: () => void
}

const lineClass = (l: string) =>
  /^\s*✗|refused|Not approved|not approved|✗ /.test(l) ? 'text-red-600 dark:text-red-400' : /^\s*⚠/.test(l) ? 'text-amber-600 dark:text-amber-300' : /^\s*✓|→ tests passed|passed$/.test(l) ? 'text-emerald-700 dark:text-emerald-400' : /^\[\d/.test(l) ? 'text-primary' : ''

export function BottomPanel(p: BottomPanelProps) {
  const errors = p.problems.filter((x) => x.level === 'error').length
  const tabs: [PanelTab, string][] = [['problems', `Problems${p.problems.length ? ` ${p.problems.length}` : ''}`], ['output', 'Output'], ['terminal', 'Terminal'], ['console', 'Console']]
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = bodyRef.current
    if (el && p.tab !== 'terminal') el.scrollTop = el.scrollHeight
  }, [p.tab, p.output.length, p.console.length])

  const pre = (lines: string[], testid: string) => (
    <pre className="whitespace-pre-wrap break-words font-mono text-[12px] leading-[18px]" data-testid={testid}>
      {lines.map((l, i) => <div key={i} className={lineClass(l)}>{l || ' '}</div>)}
    </pre>
  )

  return (
    <section className="flex h-full flex-col bg-white dark:bg-slate-950" aria-label="Panel">
      <div className="flex h-8 shrink-0 items-center gap-4 border-b border-slate-200 px-3 dark:border-slate-800" role="tablist">
        {tabs.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={p.tab === id} onClick={() => p.onTab(id)} className={`-mb-px h-8 border-b-2 text-[11px] font-medium uppercase tracking-wider ${p.tab === id ? 'border-primary text-default' : 'border-transparent text-muted hover:text-default'}`}>
            {label}
            {id === 'problems' && errors > 0 && <span className="ml-1 rounded-full bg-red-600 px-1 text-[10px] text-white">{errors}</span>}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-0.5">
          {(p.tab === 'output' || p.tab === 'console') && <button type="button" className="icon-btn !p-1" aria-label="Clear" title="Clear" onClick={p.onClear}><Trash2 size={13} /></button>}
          <button type="button" className="icon-btn !p-1" aria-label="Close panel" onClick={p.onClose}><X size={14} /></button>
        </div>
      </div>
      <div ref={bodyRef} className={`min-h-0 flex-1 ${p.tab === 'terminal' ? 'overflow-hidden p-1' : 'overflow-y-auto px-3 pb-2'}`}>
        {p.tab === 'problems' && (
          <ul className="text-[12px]" data-testid="pg-problems">
            {p.problems.map((x, i) => (
              <li key={i}>
                <button type="button" className="flex w-full items-start gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => p.onProblem(x)}>
                  {x.level === 'error' ? <CircleX size={13} className="mt-0.5 shrink-0 text-red-600" /> : <TriangleAlert size={13} className="mt-0.5 shrink-0 text-amber-500" />}
                  <span className="min-w-0 flex-1">{x.text}</span>
                  <span className="shrink-0 font-mono text-[11px] text-muted">{x.path}{x.line ? `:${x.line}` : ''} · {x.source}</span>
                </button>
              </li>
            ))}
            {!p.problems.length && <li className="py-1 text-muted">No problems. Every endpoint file defines an endpoint, every test event points at one, every variable has a value, and the last runs passed the system check.</li>}
          </ul>
        )}
        {p.tab === 'output' && pre(p.output, 'pg-output')}
        {p.tab === 'console' && pre(p.console, 'pg-console')}
        {p.tab === 'terminal' && <TerminalView url={p.terminalUrl} dark={p.dark} note={p.terminalNote} />}
      </div>
    </section>
  )
}
